<script setup>
import { ref } from "vue";
import http from "@/http";

import Card from "@/baseComponents/Card";
import PlusIcon from "vue-material-design-icons/Plus";
import EditIcon from "vue-material-design-icons/Pencil";
import ViewIcon from "vue-material-design-icons/EyeOutline";
import InoculantsIcon from "vue-material-design-icons/Sprout";
import BiodefensiveIcon from "vue-material-design-icons/Leaf";

const loading = ref(false);
const loadingLoadMore = ref(false);
const loadingFiltered = ref(false);
const reports = ref([]);
const allInoculantReportsFetched = ref(false);
const allBiodefensiveReportsFetched = ref(false);
const page = ref(1);
const take = ref(16);
const selectedStatus = ref("");

const getInoculantReports = () => {
  if (allInoculantReportsFetched.value) return;
  return http.get(
    `report/list?orderBy=-timestamp&page=${page.value}&take=${take.value}&status=${selectedStatus.value}`
  );
};
const getBiodefensiveReports = () => {
  if (allBiodefensiveReportsFetched.value) return;
  return http.get(
    `report/biodefensive/list?orderBy=-timestamp&page=${page.value}&take=${take.value}&status=${selectedStatus.value}`
  );
};

const getReports = () => {
  if (reports.value.length > 0) loadingLoadMore.value = true;
  Promise.all([getInoculantReports(), getBiodefensiveReports()])
    .then((responses) => {
      const inoculantResponse = responses[0];
      const biodefensiveResponse = responses[1];

      if (inoculantResponse) {
        reports.value = [
          ...reports.value,
          ...inoculantResponse.data?.list?.map((report) => {
            if (report.status === "Em análise 5P2R")
              report.status = "Em análise";
            report.type = "Inoculant";
            return report;
          }),
        ];
      }
      if (biodefensiveResponse) {
        reports.value = [
          ...reports.value,
          ...biodefensiveResponse.data?.list?.map((report) => {
            if (report.status === "Em análise 5P2R")
              report.status = "Em análise";
            report.type = "Biodefensive";
            return report;
          }),
        ];
      }

      if (page.value >= inoculantResponse?.data?.pages)
        allInoculantReportsFetched.value = true;
      if (page.value >= biodefensiveResponse?.data?.pages)
        allBiodefensiveReportsFetched.value = true;

      if (
        page.value < inoculantResponse?.data?.pages ||
        page.value < biodefensiveResponse?.data?.pages
      )
        page.value++;
    })
    .finally(() => {
      loading.value = false;
      loadingLoadMore.value = false;
      loadingFiltered.value = false;
    });
};
getReports();

const changeFilterStatus = () => {
  page.value = 1;
  reports.value = [];
  loadingFiltered.value = true;
  allInoculantReportsFetched.value = false;
  allBiodefensiveReportsFetched.value = false;
  getReports();
};
</script>

<template>
  <Card class="animated fadeInLeft mt-3-mobile" style="animation-delay: 150ms">
    <div class="card-header">
      <img class="mobile-hidden" src="@/assets/images/send.svg" alt="Envios" />
      <div class="flex-start">
        <div class="title">Meus envios</div>
        <div class="select-holder">
          <select @change="changeFilterStatus" v-model="selectedStatus">
            <option value="">Todos</option>
            <option value="Em análise 5P2R">Em análise</option>
            <option value="Alteração necessária">Alteração necessária</option>
            <option value="Aprovado">Aprovado</option>
            <option value="Reprovado">Reprovado</option>
          </select>
        </div>
      </div>
      <div class="flex ml-auto-desktop">
        <router-link to="/gerenciar-report/novo" class="btn icon border mt-0">
          <span>Registrar novo relatório</span>
          <PlusIcon fillColor="var(--primary)" />
        </router-link>
      </div>
    </div>

    <div class="divider mt-2"></div>

    <div v-show="loadingFiltered" class="flex-center my-3">
      <div class="loading"></div>
    </div>

    <div v-show="reports.length <= 0 && !loadingFiltered" class="empty mt-2">
      Nenhum registro encontrado.
    </div>
    <div class="table-holder mt-2">
      <table
        v-show="reports.length > 0 && !loadingFiltered"
        class="table-reports"
      >
        <thead>
          <tr>
            <th>Período</th>
            <th>Segmento</th>
            <th>Volume</th>
            <th>Responsável</th>
            <th>Enviado em</th>
            <th>Status</th>
            <th style="text-align: center">Acessar</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="report in reports" :key="report.id">
            <td>{{ report.period.name }}</td>
            <td>
              <div class="sales-category-type">
                <div
                  style="display: contents"
                  v-if="report.type === 'Inoculant'"
                >
                  <InoculantsIcon :size="24" fillColor="var(--secondary)" />
                  <span class="color-secondary">Inoculantes</span>
                </div>
                <div style="display: contents" v-else>
                  <BiodefensiveIcon :size="22" fillColor="var(--primary)" />
                  <span>Biodefensivos</span>
                </div>
              </div>
            </td>
            <td>
              <span class="color-primary">
                <b>{{ report.totalVolume }}</b> litros
              </span>
            </td>
            <td>{{ report.userName }}</td>
            <td>{{ $filters.dateFilter(report.timestamp) }}</td>
            <td>
              <div
                class="report-status"
                :class="{
                  alteration: report.status == 'Alteração necessária',
                  onapproved:
                    report.status == 'Aprovado' &&
                    report.period.status == 'Em andamento ANPII',
                  approved:
                    report.status == 'Aprovado' &&
                    report.period.status == 'Aprovado',
                  disapproved: report.status == 'Reprovado',
                }"
              >
                <div class="status">
                  <div class="circle">
                    <span v-show="report.status == 'Alteração necessária'"
                      >!</span
                    >
                  </div>
                  <span
                    v-if="
                      report.status == 'Aprovado' &&
                      report.period.status === 'Em andamento 5P2R'
                    "
                  >
                    Em análise
                  </span>
                  <span
                    v-else-if="
                      report.status == 'Aprovado' &&
                      report.period.status === 'Em andamento ANPII'
                    "
                  >
                    Em aprovação
                  </span>
                  <span v-else>{{ report.status }}</span>
                </div>
              </div>
            </td>
            <td style="text-align: center" class="btn-action">
              <router-link
                :to="`/gerenciar-report/${report.id}?report=${report.type}`"
              >
                <EditIcon
                  v-if="report.status == 'Alteração necessária'"
                  fillColor="var(--orange)"
                />
                <ViewIcon v-else fillColor="var(--primary)" />
              </router-link>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      class="flex-center my-2"
      v-show="
        (!allBiodefensiveReportsFetched || !allInoculantReportsFetched) &&
        !loadingFiltered
      "
    >
      <div @click="getReports" class="btn border load-more">
        <span v-show="!loadingLoadMore">Carregar mais</span>
        <div v-show="loadingLoadMore" class="loading light-blue"></div>
      </div>
    </div>
  </Card>
</template>

<style lang="scss" scoped>
.card-header {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 50px;
  @media only screen and (max-width: 720px) {
    flex-direction: column;
    gap: 30px;
    align-items: flex-start;
  }
  img {
    position: absolute;
    top: -65px;
    object-fit: cover;
  }
  .flex-start {
    gap: 40px;
  }
  .title {
    margin-left: 140px;
    @media only screen and (max-width: 720px) {
      margin-left: 0px;
      margin-right: auto;
    }
  }
  .select-holder {
    select {
      font-family: fontRegular;
      font-size: 1em;
      color: var(--gray);
      cursor: pointer;
      padding-right: 4px;
    }
  }
}

.table-holder {
  position: relative;
  width: calc(100% + 12px);
  max-height: 200px;
  overflow: auto;
  padding: 0px 12px 12px 0px;
  &::-webkit-scrollbar {
    width: 0.2em;
    height: 0.2em;
  }

  &::-webkit-scrollbar-track {
    -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
    box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--primary);
    outline: 1px solid slategrey;
    border-radius: 8px;
  }
  @media only screen and (min-width: 1160px) {
    overflow-x: initial;
  }
}

table {
  position: relative;
  font-family: fontRegular;
  font-size: 0.933em;
  border-collapse: collapse;
  width: 100%;
  color: var(--dark3);
  td,
  th {
    padding: 4px;
    white-space: nowrap;
  }

  td:first-child,
  th:first-child {
    border-top-left-radius: 6px;
    border-bottom-left-radius: 6px;
  }

  td:last-child,
  th:last-child {
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
  }

  tr:nth-child(odd) {
    background-color: #f5f5f5;
  }

  th {
    padding-top: 6px;
    padding-bottom: 6px;
    text-align: left;
    background-color: var(--primary);
    color: white;
  }

  .btn-action {
    padding: 0px;
    a {
      padding: 4px;
      display: flex;
      justify-content: center;
      width: 100%;
      height: 100%;
    }
    span {
      display: flex;
    }
    &:hover {
      border-top-left-radius: 6px;
      border-bottom-left-radius: 6px;
      background-color: #eeeeee;
    }
  }

  .sales-category-type {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 4px;
    width: fit-content;
    margin-top: 6px;
    span {
      font-size: 0.933em;
      color: var(--primary);
    }
  }

  .report-status {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: start;
    width: fit-content;
    .status {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: 10px;
      span {
        font-size: 0.933em;
      }
    }
    .necessary-change {
      color: var(--orange) !important;
      font-family: fontLight;
      font-size: 0.933em;
    }
    &.alteration {
      .circle {
        background-color: var(--orange);
      }
      span {
        color: var(--orange);
      }
    }
    &.onapproved {
      .circle {
        background-color: #4000e2;
      }
      span {
        color: #4000e2;
      }
    }
    &.approved {
      .circle {
        background-color: var(--secondary);
      }
      span {
        color: var(--secondary);
      }
    }
    &.disapproved {
      .circle {
        background-color: var(--red2);
      }
      span {
        color: var(--red2);
      }
    }
    .circle {
      display: flex;
      justify-content: center;
      align-items: center;
      position: relative;
      width: 0.933em;
      height: 0.933em;
      border-radius: 100%;
      background-color: var(--light-blue);
      span {
        color: #fff;
        z-index: 2;
        font-size: 0.933em;
        font-family: fontBold;
      }
    }
    span {
      color: var(--light-blue);
    }
    .icon {
      position: absolute;
      right: 0;
      top: 0;
    }
  }
}

.flex-start {
  position: relative;
  width: fit-content;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 50px;
  @media only screen and (min-width: 721px) and (max-width: 1159px) {
    gap: 20px;
  }
  @media only screen and (max-width: 720px) {
    width: 100%;
    gap: 10px;
  }
}

.empty {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  font-size: 1.2em;
  height: 100px;
  color: var(--dark4);
}

.load-more {
  border: 1px solid var(--light-blue) !important;
  span {
    color: var(--light-blue) !important;
  }
}
</style>
